import React, { useEffect } from 'react';
import './TermsOfService.css';

const TermsOfService = () => {
    useEffect(() => {
        document.title = 'Terms of Service | Avenis Global';
    }, []);

    return (
        <div className="terms-page">
            <div className="terms-hero">
                <div className="container">
                    <h1>Terms of Service</h1>
                    <p>
                        Please read these terms carefully before using our
                        services. By using{' '}
                        <span className="brand-gradient">Avenis Global</span>{' '}
                        services, you agree to these terms.
                    </p>
                    <p className="last-updated">Last updated: February 2025</p>
                </div>
            </div>

            <div className="container">
                <div className="terms-content">
                    <div className="terms-section">
                        <h2>1. Acceptance of Terms</h2>
                        <p>
                            By accessing and using{' '}
                            <span className="brand-gradient">
                                Avenis Global
                            </span>
                            's services, you accept and agree to be bound by the
                            terms and provision of this agreement. If you do not
                            agree to abide by the above, please do not use this
                            service.
                        </p>
                    </div>

                    <div className="terms-section">
                        <h2>2. Description of Service</h2>
                        <p>
                            <span className="brand-gradient">
                                Avenis Global
                            </span>{' '}
                            provides authorized retail partnership services,
                            including but not limited to:
                        </p>
                        <ul>
                            <li>
                                MAP (Minimum Advertised Price) compliance
                                monitoring and enforcement
                            </li>
                            <li>Brand-safe e-commerce solutions</li>
                            <li>
                                Enterprise-grade data protection and security
                            </li>
                            <li>Digital marketing and e-commerce consulting</li>
                            <li>Web design and development services</li>
                            <li>SEO optimization and analytics</li>
                        </ul>
                    </div>

                    <div className="terms-section">
                        <h2>3. User Responsibilities</h2>
                        <p>As a user of our services, you agree to:</p>
                        <ul>
                            <li>
                                Provide accurate and complete information when
                                required
                            </li>
                            <li>
                                Maintain the confidentiality of your account
                                credentials
                            </li>
                            <li>Use our services only for lawful purposes</li>
                            <li>
                                Comply with all applicable laws and regulations
                            </li>
                            <li>Respect intellectual property rights</li>
                            <li>
                                Not engage in any activities that may harm our
                                systems or other users
                            </li>
                        </ul>
                    </div>

                    <div className="terms-section">
                        <h2>4. MAP Compliance and Brand Protection</h2>
                        <p>
                            Our MAP compliance services are designed to protect
                            brand integrity and ensure fair pricing across all
                            channels. By using our services, you agree to:
                        </p>
                        <ul>
                            <li>
                                Comply with all MAP policies established by
                                brand partners
                            </li>
                            <li>Provide accurate pricing information</li>
                            <li>
                                Cooperate with compliance monitoring and
                                enforcement activities
                            </li>
                            <li>
                                Report any violations or suspicious activities
                            </li>
                            <li>
                                Maintain transparency in all business dealings
                            </li>
                        </ul>
                    </div>

                    <div className="terms-section">
                        <h2>5. Data Protection and Privacy</h2>
                        <p>
                            We are committed to protecting your data and
                            privacy. Our data practices are governed by our
                            Privacy Policy, which is incorporated into these
                            terms by reference. We implement enterprise-grade
                            security measures to protect your information.
                        </p>
                    </div>

                    <div className="terms-section">
                        <h2>6. Intellectual Property Rights</h2>
                        <p>
                            All content, trademarks, and intellectual property
                            rights in our services are owned by{' '}
                            <span className="brand-gradient">
                                Avenis Global
                            </span>{' '}
                            or our licensors. You may not:
                        </p>
                        <ul>
                            <li>
                                Copy, modify, or distribute our content without
                                permission
                            </li>
                            <li>
                                Use our trademarks or logos without
                                authorization
                            </li>
                            <li>
                                Reverse engineer or attempt to extract source
                                code
                            </li>
                            <li>
                                Create derivative works based on our services
                            </li>
                        </ul>
                    </div>

                    <div className="terms-section">
                        <h2>7. Payment Terms</h2>
                        <p>Payment terms for our services are as follows:</p>
                        <ul>
                            <li>
                                Fees are due according to the agreed payment
                                schedule
                            </li>
                            <li>
                                Late payments may result in service suspension
                            </li>
                            <li>
                                All fees are non-refundable unless otherwise
                                specified
                            </li>
                            <li>
                                We reserve the right to change pricing with 30
                                days notice
                            </li>
                            <li>
                                Disputed charges must be reported within 30 days
                            </li>
                        </ul>
                    </div>

                    <div className="terms-section">
                        <h2>8. Service Availability</h2>
                        <p>
                            While we strive to provide continuous service
                            availability, we cannot guarantee uninterrupted
                            access. We may:
                        </p>
                        <ul>
                            <li>
                                Perform scheduled maintenance with advance
                                notice
                            </li>
                            <li>
                                Suspend services for security or compliance
                                reasons
                            </li>
                            <li>
                                Modify or discontinue services with reasonable
                                notice
                            </li>
                            <li>
                                Implement emergency measures to protect our
                                systems
                            </li>
                        </ul>
                    </div>

                    <div className="terms-section">
                        <h2>9. Limitation of Liability</h2>
                        <p>
                            To the maximum extent permitted by law, Avenis
                            Global shall not be liable for any indirect,
                            incidental, special, consequential, or punitive
                            damages, including but not limited to loss of
                            profits, data, or business opportunities, arising
                            from your use of our services.
                        </p>
                    </div>

                    <div className="terms-section">
                        <h2>10. Indemnification</h2>
                        <p>
                            You agree to indemnify and hold harmless Avenis
                            Global from any claims, damages, or expenses arising
                            from:
                        </p>
                        <ul>
                            <li>Your use of our services</li>
                            <li>Your violation of these terms</li>
                            <li>Your violation of any third-party rights</li>
                            <li>
                                Any content you submit or transmit through our
                                services
                            </li>
                        </ul>
                    </div>

                    <div className="terms-section">
                        <h2>11. Termination</h2>
                        <p>
                            Either party may terminate this agreement at any
                            time with written notice. We may immediately
                            terminate your access if you:
                        </p>
                        <ul>
                            <li>Violate these terms of service</li>
                            <li>Engage in fraudulent or illegal activities</li>
                            <li>Fail to pay required fees</li>
                            <li>
                                Pose a security risk to our systems or other
                                users
                            </li>
                        </ul>
                    </div>

                    <div className="terms-section">
                        <h2>12. Governing Law and Dispute Resolution</h2>
                        <p>
                            These terms are governed by the laws of Turkey. Any
                            disputes arising from these terms or our services
                            shall be resolved through binding arbitration in
                            Istanbul, Turkey, unless otherwise agreed by both
                            parties.
                        </p>
                    </div>

                    <div className="terms-section">
                        <h2>13. Changes to Terms</h2>
                        <p>
                            We reserve the right to modify these terms at any
                            time. We will notify users of material changes via
                            email or through our website. Continued use of our
                            services after changes constitutes acceptance of the
                            new terms.
                        </p>
                    </div>

                    <div className="terms-section">
                        <h2>14. Contact Information</h2>
                        <p>
                            If you have any questions about these Terms of
                            Service, please contact us:
                        </p>
                        <div className="contact-info">
                            <p>
                                <strong>Email:</strong>{' '}
                                <a
                                    href="mailto:compliance@avenisglobal.com"
                                    className="privacy-contact-link"
                                >
                                    compliance@avenisglobal.com
                                </a>
                            </p>
                            <p>
                                <strong>Phone:</strong>{' '}
                                <a
                                    href="tel:+1"
                                    className="privacy-contact-link"
                                >
                                    +1
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsOfService;
